import React from "react";
import entete from "../../../assets/img/entete_pool.png";
import BarreTop from "../../../components/docs/BarreTop";
import { DocumentFooter } from "../../../components/docs/DocumentFooter";
import dayjs from "dayjs";
import "dayjs/locale/fr";

dayjs.locale("fr");

const NAVY = "#242166";

const displayName = (user) => {
    if (!user) return "Inconnu";
    const n = `${user.personne?.prenom || user.prenom || ""} ${user.personne?.nom || user.nom || ""}`.trim();
    return n || user.username || "Inconnu";
};

const initialsOf = (user) => {
    const n = displayName(user);
    return n.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";
};

const isSameDay = (a, b) => dayjs(a).format("YYYY-MM-DD") === dayjs(b).format("YYYY-MM-DD");

/**
 * Document imprimable du FIL DE DISCUSSION d'un dossier sinistre, sur l'en-tête PoolIns.
 * Présentation « conversation » (avatars + bulles) plutôt qu'un tableau. Exporté via html2pdf.
 */
const DiscussionSinistreDoc = React.forwardRef(({ sinistre, messages = [] }, ref) => {
    const numero = sinistre?.numSinistre || sinistre?.numeroSinistreM || "-";
    const souscripteur = sinistre?.contrat?.souscripteur
        ? `${sinistre.contrat.souscripteur.nom || ""} ${sinistre.contrat.souscripteur.prenom || ""}`.trim()
        : "";

    return (
        <div
            ref={ref}
            style={{
                width: "210mm",
                background: "#fff",
                fontFamily: '"Roboto", Arial, sans-serif',
                color: "#1f2937",
                boxSizing: "border-box",
                padding: "9mm 13mm",
            }}
        >
            {/* En-tête PoolIns */}
            <div style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <img src={entete} alt="PoolIns" style={{ height: 62 }} />
                </div>
                <BarreTop />
            </div>

            {/* Titre */}
            <div style={{ textAlign: "center", borderTop: `2px solid ${NAVY}`, borderBottom: `2px solid ${NAVY}`, padding: "6px 0", marginBottom: 8 }}>
                <div style={{ fontSize: 16, fontWeight: "bold", color: NAVY, letterSpacing: 1 }}>FIL DE DISCUSSION</div>
            </div>

            {/* Bandeau dossier */}
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6, fontSize: 11, color: "#4b5563", marginBottom: 16 }}>
                <div>
                    <strong style={{ color: NAVY }}>Sinistre N° :</strong> {numero}
                    {souscripteur ? <span> &nbsp;·&nbsp; <strong style={{ color: NAVY }}>Assuré :</strong> {souscripteur}</span> : null}
                </div>
                <div>{messages.length} message(s) · exporté le {dayjs().format("DD/MM/YYYY à HH:mm")}</div>
            </div>

            {/* Conversation */}
            {messages.length === 0 ? (
                <div style={{ textAlign: "center", color: "#9ca3af", padding: 30, fontSize: 13 }}>Aucun message dans ce fil.</div>
            ) : (
                messages.map((m, i) => {
                    const showDate = i === 0 || !isSameDay(messages[i - 1].createdAt, m.createdAt);
                    const modifie = m.updatedAt && !dayjs(m.updatedAt).isSame(dayjs(m.createdAt));
                    const mentions = (m.mentions || []).map(displayName).filter(Boolean);
                    return (
                        <React.Fragment key={m.codeId || i}>
                            {showDate && (
                                <div style={{ textAlign: "center", margin: "14px 0 10px" }}>
                                    <span style={{ fontSize: 11, color: "#6b7280", background: "#f1f2f8", borderRadius: 12, padding: "3px 14px" }}>
                                        {dayjs(m.createdAt).format("dddd D MMMM YYYY")}
                                    </span>
                                </div>
                            )}
                            <div style={{ display: "flex", gap: 10, marginBottom: 10, breakInside: "avoid", pageBreakInside: "avoid" }}>
                                <div style={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0, background: "#EDECF8", color: NAVY, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13 }}>
                                    {initialsOf(m.auteur)}
                                </div>
                                <div style={{ flex: 1, minWidth: 0, background: "#f7f8fc", border: "1px solid #e7e8f2", borderRadius: "4px 12px 12px 12px", padding: "9px 13px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, marginBottom: 3 }}>
                                        <span style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>{displayName(m.auteur)}</span>
                                        <span style={{ fontSize: 10.5, color: "#9ca3af", whiteSpace: "nowrap" }}>
                                            {dayjs(m.createdAt).format("HH:mm")}{modifie ? " · modifié" : ""}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                        {m.contenu}
                                    </div>
                                    {mentions.length > 0 && (
                                        <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 5 }}>
                                            {mentions.map((n, idx) => (
                                                <span key={idx} style={{ fontSize: 10.5, color: NAVY, background: "#EDECF8", borderRadius: 10, padding: "1px 8px" }}>@ {n}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })
            )}

            {/* Pied de page PoolIns */}
            <div style={{ marginTop: 18 }}>
                <DocumentFooter />
            </div>
        </div>
    );
});

DiscussionSinistreDoc.displayName = "DiscussionSinistreDoc";

export default DiscussionSinistreDoc;
